import * as PIXI from 'pixi.js';
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { shallow } from 'zustand/shallow';
import { useBattleStore } from '@/store/useBattleStore';
import { BattleStatus, FactionType, type BattleUnit, type TurnPool } from '@/core/types/battle.types';
import { getUnitTotalHp } from '@/core/utils/battle.utils';
import { BattleBus } from '@/events/Events';
import gsap from 'gsap';
import portraitsPng from '@assets/portraits.png';

type Direction = 'horizontal' | 'vertical';

interface SkillBarConfig {
    direction: Direction;
    portraitSize: number;
    portraitGap: number;
    poolGap: number;
    colors: {
        player: {
            primary: number;
            accent: number;
        };
        enemy: {
            primary: number;
            accent: number;
        };
        neutral: {
            primary: number;
            accent: number;
        };
        finished: number;
        background: number;
        text: number;
        border: number;
    };
    portraitOpacity: number;
    activePoolGlow: number;
}

const DEFAULT_CONFIG: SkillBarConfig = {
    direction: 'vertical',
    portraitSize: 64,
    portraitGap: 8,
    poolGap: 12,
    colors: {
        player: {
            primary: 0xb88101,
            accent: 0xdcb14d,
        },
        enemy: {
            primary: 0x8a0d00,
            accent: 0xff8383,
        },
        neutral: {
            primary: 0x4a4a4a,
            accent: 0x2a2a2a,
        },
        finished: 0x808080,
        background: 0x1F211A,
        text: 0xd8c7a0,
        border: 0x8c6a2b
    },
    portraitOpacity: 1.0,
    activePoolGlow: 1.2,
};

interface UnitPortrait {
    container: Container;
    background: Graphics;
    border: Graphics;
    label: Container;
    unitId: string;
}

export class TurnQueueRenderer extends Container {
    private config: SkillBarConfig;
    private portraits: Map<string, UnitPortrait> = new Map();
    private hpBars: Map<string, { bar: Graphics; isFinished: boolean }> = new Map();
    private hpAnimValues: Map<string, { percent: number }> = new Map();
    private hpTweens: Map<string, gsap.core.Tween> = new Map();

    public layoutHeight: number = 0;
    public layoutWidth: number = 0;

    private unsubscribe: (() => void) | null = null;

    public onLayoutChange?: () => void;
    public onUnitClick: ((unitId: string) => void) | null = null;

    constructor(config: Partial<SkillBarConfig> = {}) {
        super();
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.setupStoreSubscription();
    }

    private setupStoreSubscription(): void {
        this.unsubscribe = useBattleStore.subscribe(
            (state) => {
                const unitHps = [...state.turnQueue, ...state.finishedPools]
                    .flatMap(p => p.unitIds)
                    .map(id => {
                        const u = state.units.get(id);
                        return u ? getUnitTotalHp(u) : 0;
                    });
                return [
                    state.selectedUnitId,
                    state.turnQueue,
                    state.finishedPools,
                    state.status,
                    unitHps.join(','),
                ] as const;
            },
            ([selectedUnitId, turnQueue, finishedPools, status]) => {
                this.render(selectedUnitId, turnQueue, finishedPools, status);
            },
            { equalityFn: shallow }
        );
    }

    private render(
        selectedUnitId: string | null,
        turnQueue: TurnPool[],
        finishedPools: TurnPool[],
        status: BattleStatus
    ): void {
        const allQueueIds = new Set([
            ...turnQueue.flatMap(p => p.unitIds),
            ...finishedPools.flatMap(p => p.unitIds),
        ]);

        for (const [id, tween] of this.hpTweens) {
            if (!allQueueIds.has(id)) {
                tween.kill();
                this.hpTweens.delete(id);
                this.hpAnimValues.delete(id);
            }
        }

        this.clearPortraits();
        if (status !== BattleStatus.Playing) return;
        let offset = 0;

        turnQueue.forEach((pool, poolIdx) => {
            const visibleUnits = pool.unitIds.filter(id => id !== selectedUnitId);
            if (visibleUnits.length === 0) return;

            const isCurrentPool = poolIdx === 0;
            const visiblePool: TurnPool = { ...pool, unitIds: visibleUnits };

            const poolSize = this.renderPool(visiblePool, isCurrentPool, false, offset);
            offset += poolSize + this.config.poolGap;
        });

        finishedPools.forEach((pool) => {
            const poolSize = this.renderPool(pool, false, true, offset);
            offset += poolSize + this.config.poolGap;
        });

        const finalSize = offset > 0 ? offset - this.config.poolGap : 0;
        this.layoutHeight = this.config.direction === 'vertical' ? finalSize : this.config.portraitSize;
        this.layoutWidth = this.config.direction === 'vertical' ? this.config.portraitSize : finalSize;

        this.onLayoutChange?.();
    }

    private renderPool(
        pool: TurnPool,
        isActive: boolean,
        isFinished: boolean,
        offset: number
    ): number {
        const { portraitSize, direction } = this.config;
        const unitCount = pool.unitIds.length;

        pool.unitIds.forEach((unitId, index) => {
            const pos = {
                isFirst: index === 0,
                isLast: index === unitCount - 1,
                isSingle: unitCount === 1
            };

            let corners: [number, number, number, number] = [0, 0, 0, 0];
            if (pos.isSingle) {
                corners = [1, 1, 1, 1];
            } else if (direction === 'vertical') {
                if (pos.isFirst) corners = [0, 0, 1, 1];
                else if (pos.isLast) corners = [1, 1, 0, 0];
            } else {
                if (pos.isFirst) corners = [1, 0, 0, 1];
                else if (pos.isLast) corners = [0, 1, 1, 0];
            }

            const portrait = this.createPortrait(
                unitId,
                pool.faction,
                isFinished,
                !isFinished,
                corners,
                isActive,
                pos
            );

            this.portraits.set(unitId, portrait);
            const stepInsidePool = index * portraitSize;

            if (direction === 'vertical') {
                portrait.container.x = 0;
                portrait.container.y = -(offset + stepInsidePool + portraitSize);
            } else {
                portrait.container.x = offset + stepInsidePool;
                portrait.container.y = 0;
            }

            this.addChild(portrait.container);
        });

        return unitCount * portraitSize;
    }

    private createPortrait(
        unitId: string,
        faction: FactionType,
        isFinished: boolean,
        isInteractive: boolean,
        corners: [number, number, number, number],
        isActivePool: boolean,
        pos: { isFirst: boolean; isLast: boolean; isSingle: boolean }
    ): UnitPortrait {
        const container = new Container();
        const { portraitSize, portraitGap, direction } = this.config;

        const unit = this.getUnitData(unitId);
        const borderColor = this.getPortraitBorderColor(faction, isActivePool);
        const borderWidth = portraitGap / 2;

        const actualBorderColor = !isFinished
            ? this.brightenColor(borderColor, this.config.activePoolGlow)
            : borderColor;

        const border = new Graphics();
        const borderRadius = 8;

        this.drawComplexRect(
            border,
            portraitSize,
            portraitSize,
            borderRadius,
            corners,
            actualBorderColor,
            pos,
            direction
        );

        container.addChild(border);

        const inset = borderWidth;
        const innerSize = portraitSize - inset * 2;
        const innerRadius = Math.max(0, borderRadius - inset);

        const background = new Graphics();
        background.roundRect(inset, inset, innerSize, innerSize, innerRadius);
        background.fill(this.config.colors.background);
        background.stroke({
            width: 1,
            color: this.config.colors.border,
            alpha: 0.8,
            alignment: 0.5
        });

        const portraitContent = this.createPortraitContent(unitId, unit, borderWidth, isFinished);

        const portraitContentContainer = new Container();
        portraitContentContainer.addChild(portraitContent);
        portraitContentContainer.pivot.set(portraitSize / 2, portraitSize / 2);
        portraitContentContainer.position.set(portraitSize / 2, portraitSize / 2);

        container.addChild(portraitContentContainer);

        if (isInteractive && !isFinished) {
            container.eventMode = 'static';
            container.on('pointerdown', () => this.handlePortraitClick(unitId));
            container.on('pointerover', () => { portraitContentContainer.scale.set(1.1); });
            container.on('pointerout', () => { portraitContentContainer.scale.set(1.0); });
        }

        return {
            container,
            background,
            border,
            label: portraitContent,
            unitId,
        };
    }

    private createPortraitContent(
        unitId: string,
        unit: BattleUnit | null,
        borderWidth: number,
        isFinished: boolean
    ): Container {
        const { portraitSize } = this.config;
        const wrapper = new Container();

        // Portrait sprite
        if (unit?.unitPortrait) {
            const { row, col } = unit.unitPortrait;
            const TILE = 96;

            try {
                const base = PIXI.Texture.from(portraitsPng);
                const frame = new PIXI.Texture({
                    source: base.source,
                    frame: new PIXI.Rectangle(col * TILE, row * TILE, TILE, TILE),
                });

                const sprite = new PIXI.Sprite(frame);
                const inset = borderWidth;
                const spriteWidth = portraitSize - inset * 2;

                sprite.x = inset;
                sprite.y = inset;
                sprite.width = spriteWidth;
                sprite.height = spriteWidth;
                sprite.eventMode = 'none';

                if (isFinished) {
                    sprite.alpha = 0.4;
                    sprite.tint = this.config.colors.finished;
                }

                if (unit.faction === FactionType.Enemy) {
                    sprite.scale.x *= -1;
                    sprite.x = inset + spriteWidth;
                }

                wrapper.addChild(sprite);
            } catch (e) {
                console.warn('[TurnQueueRenderer] Failed to load portrait:', unitId, e);
                wrapper.addChild(this.createPortraitPlaceholder(unitId, unit));
            }
        } else {
            wrapper.addChild(this.createPortraitPlaceholder(unitId, unit));
        }

        // HP Bar + Count
        if (unit) {
            const barWidth = 6;
            const inset = borderWidth;
            const spriteWidth = portraitSize - inset * 2;
            const hpPercent = (getUnitTotalHp(unit) / unit.maxUnitHp) * 100;

            const hpBar = new Graphics();
            this.hpBars.set(unitId, { bar: hpBar, isFinished });

            if (!this.hpAnimValues.has(unitId)) {
                this.hpAnimValues.set(unitId, { percent: hpPercent });
            }

            const animVal = this.hpAnimValues.get(unitId)!;
            const prevPercent = animVal.percent;

            this.drawHpBar(hpBar, animVal.percent, inset, barWidth, spriteWidth, isFinished);
            wrapper.addChild(hpBar);

            if (Math.abs(prevPercent - hpPercent) > 0.1) {
                this.hpTweens.get(unitId)?.kill();

                const tween = gsap.to(animVal, {
                    percent: hpPercent,
                    duration: 0.6,
                    ease: 'power2.out',
                    onUpdate: () => {
                        const entry = this.hpBars.get(unitId);
                        if (entry) {
                            this.drawHpBar(entry.bar, animVal.percent, inset, barWidth, spriteWidth, entry.isFinished);
                        }
                    },
                    onComplete: () => {
                        this.hpTweens.delete(unitId);
                    }
                });

                this.hpTweens.set(unitId, tween);
            }

            // Count badge
            // const style = new TextStyle({
            //     fontFamily: 'KyivType, sans-serif',
            //     fontSize: 13,
            //     fontWeight: 'bold',
            //     fill: isFinished ? 0x888888 : 0xc9bda8,
            //     stroke: { color: 0x000000, width: 2 },
            // });

            // const countText = new Text({ text: String(unit.count), style });
            const countText = new PIXI.BitmapText({
                        text: String(unit.count),
                        style: { 
                            fontFamily: 'KyivTypeBitmap', 
                            fontSize: 14,
                            
                        },
                        tint: isFinished ? 0x888888 : 0xc9bda8,
                        // resolution: 2,
                    });
            countText.anchor.set(1, 0);
            countText.position.set(portraitSize - 3, 1);
            wrapper.addChild(countText);
        }

        return wrapper;
    }

    private drawHpBar(
        hpBar: Graphics,
        percent: number,
        inset: number,
        barWidth: number,
        spriteWidth: number,
        isFinished: boolean
    ): void {
        const filledHeight = Math.round(spriteWidth * (percent / 100));
        const hpColor = isFinished
            ? 0x888888
            : percent > 60 ? 0x4ba14c : percent > 30 ? 0xff9800 : 0xf44336;

        hpBar.clear();
        hpBar.rect(inset - barWidth / 2, inset, barWidth, spriteWidth);
        hpBar.fill({ color: 0x1a1a1a, alpha: 0.8 });
        hpBar.rect(inset - barWidth / 2, inset + spriteWidth - filledHeight, barWidth, filledHeight);
        hpBar.fill({ color: hpColor, alpha: isFinished ? 0.6 : 0.9 });
    }

    private createPortraitPlaceholder(unitId: string, unit: BattleUnit | null): PIXI.BitmapText {
        const { portraitSize } = this.config;
        const displayText = unit?.unitId.substring(0, 2).toUpperCase() || unitId.charAt(0).toUpperCase();

        const style = new TextStyle({
            fontFamily: 'KyivType, sans-serif',
            fontSize: 18,
            fontWeight: 'bold',
            fill: this.config.colors.text,
            stroke: { color: 0x0a0a0a, width: 1 },
        });

        const text = new PIXI.BitmapText({ text: displayText, style });
        text.anchor.set(0.5);
        text.position.set(portraitSize / 2, portraitSize / 2);

        return text;
    }

    private drawComplexRect(
        g: Graphics,
        w: number,
        h: number,
        radius: number,
        corners: [number, number, number, number],
        color: number,
        pos: { isFirst: boolean; isLast: boolean; isSingle: boolean },
        direction: 'horizontal' | 'vertical'
    ): void {
        const extra = 8;
        const offset = extra / 2;

        let startX = 0;
        let startY = 0;
        let drawW = w;
        let drawH = h;

        if (direction === 'vertical') {
            startX = -offset;
            drawW = w + extra;
            if (pos.isSingle) {
                startY = -offset;
                drawH = h + extra;
            } else if (pos.isFirst) {
                startY = 0;
                drawH = h + offset;
            } else if (pos.isLast) {
                startY = -offset;
                drawH = h + offset;
            }
        } else {
            startY = -offset;
            drawH = h + extra;
            if (pos.isSingle) {
                startX = -offset;
                drawW = w + extra;
            } else if (pos.isFirst) {
                startX = -offset;
                drawW = w + offset;
            } else if (pos.isLast) {
                startX = 0;
                drawW = w + offset;
            }
        }

        const [tlR, trR, brR, blR] = corners.map(c => c * radius) as [number, number, number, number];

        g.clear();
        g.moveTo(startX + tlR, startY);
        g.lineTo(startX + drawW - trR, startY);
        if (trR > 0) g.arcTo(startX + drawW, startY, startX + drawW, startY + trR, trR);

        g.lineTo(startX + drawW, startY + drawH - brR);
        if (brR > 0) g.arcTo(startX + drawW, startY + drawH, startX + drawW - brR, startY + drawH, brR);

        g.lineTo(startX + blR, startY + drawH);
        if (blR > 0) g.arcTo(startX, startY + drawH, startX, startY + drawH - blR, blR);

        g.lineTo(startX, startY + tlR);
        if (tlR > 0) g.arcTo(startX, startY, startX + tlR, startY, tlR);

        g.fill({ color, alpha: 0.35 });
    }

    private getPortraitBorderColor(faction: FactionType, isActive: boolean): number {
        switch (faction) {
            case FactionType.Player:
                return isActive ? this.config.colors.player.accent : this.config.colors.player.primary;
            case FactionType.Enemy:
                return this.config.colors.enemy.primary;
            case FactionType.Neutral:
                return this.config.colors.neutral.primary;
            default:
                return this.config.colors.neutral.primary;
        }
    }

    private brightenColor(color: number, factor: number): number {
        const r = Math.min(255, ((color >> 16) & 0xff) * factor);
        const g = Math.min(255, ((color >> 8) & 0xff) * factor);
        const b = Math.min(255, (color & 0xff) * factor);
        return (r << 16) | (g << 8) | b;
    }

    private getUnitData(unitId: string): BattleUnit | null {
        const state = useBattleStore.getState();
        return state.units.get(unitId) || null;
    }

    private handlePortraitClick(unitId: string): void {
        BattleBus.emit("UNIT_CLICK", { id: unitId });
        BattleBus.emit('FOCUS_ON_UNIT', {unitId: unitId, force: true});
        if (this.onUnitClick) {
            this.onUnitClick(unitId);
        } else {
            console.warn(`TurnQueueRenderer: onUnitClick not assigned. UnitId: ${unitId}`);
        }
    }

    private clearPortraits(): void {
        this.portraits.forEach((portrait) => {
            portrait.container.destroy({ children: true });
        });
        this.portraits.clear();
        this.hpBars.clear();
        this.removeChildren();
    }

    public destroy(options?: any): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }

        this.hpTweens.forEach(tween => tween.kill());
        this.hpTweens.clear();
        this.hpAnimValues.clear();

        this.clearPortraits();
        super.destroy(options);
    }
}